
/**
 * RiskTreatment Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class RiskTreatmentService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'RiskTreatment listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch risk-treatment list');
        }
    }

    /**
     * Get Stats
     */
    async getStats() {
        try {
            const response = await withRetry(
                () => this.api.getStats(),
                { maxRetries: 2 }
            );
            const payload = response?.data !== undefined ? response.data : response;
            return {
                success: true,
                data: payload,
                message: response?.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch risk-treatment stats');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch risk-treatment');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'RiskTreatment oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create risk-treatment');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'RiskTreatment güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update risk-treatment');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'RiskTreatment silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete risk-treatment');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    async getExecutionStats() {
        try {
            const response = await withRetry(() => this.api.getExecutionStats(), { maxRetries: 2 });
            const payload = response?.data !== undefined ? response.data : response;
            return { success: true, data: payload };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch unified execution KPIs');
        }
    }

    async getExecutionTicket(id) {
        try {
            const response = await withRetry(() => this.api.getExecutionTicket(id), { maxRetries: 2 });
            const payload = response?.data !== undefined ? response.data : response;
            return { success: true, data: payload };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch execution ticket');
        }
    }

    async openExecutionTicket(id) {
        try {
            const response = await withRetry(() => this.api.openExecutionTicket(id), { maxRetries: 2 });
            const payload = response?.data !== undefined ? response.data : response;
            return { success: true, data: payload };
        } catch (error) {
            throw this._handleError(error, 'Failed to open execution ticket');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
